import { createI18n, localeFrom, browser } from '@nanostores/i18n';
import { persistentAtom } from '@nanostores/persistent';
import type { ComponentsJSON } from '@nanostores/i18n';

/**
 * Supported languages
 */
export type Language = 'en' | 'it';

/**
 * Persistent atom for user's language preference
 * Stored in localStorage as 'language'
 */
const localeSettings = persistentAtom<Language | undefined>('language', undefined, {
  encode: JSON.stringify,
  decode: JSON.parse,
});

/**
 * Current locale store with fallback chain:
 * 1. localStorage (user preference from localeSettings)
 * 2. Browser language detection
 * 3. Fallback to 'en'
 */
export const locale = localeFrom(
  localeSettings,
  browser<readonly Language[]>({ available: ['en', 'it'] as const, fallback: 'en' })
);

/**
 * i18n instance for loading translations
 * Translations are loaded on-demand per component
 */
export const i18n = createI18n<Language, 'en'>(locale, {
  baseLocale: 'en',
  async get(code): Promise<ComponentsJSON> {
    // Dynamically import translation files based on locale
    // English translations are defined in useTranslation.ts as base locale
    switch (code) {
      case 'it':
        return (await import('./it.json')).default as ComponentsJSON;
      default:
        // Return empty object for base locale (en) since it's defined in useTranslation.ts
        return {};
    }
  },
});

/**
 * Update URL with language parameter
 * Only sets the param if it differs from the browser default
 */
function updateURLWithLanguage(newLang: Language) {
  if (typeof window === 'undefined') return;

  const browserLang = navigator.language.split('-')[0] as Language;
  const defaultLang: Language = ['en', 'it'].includes(browserLang) ? browserLang : 'en';

  // Parse current hash
  const hashParts = window.location.hash.slice(1).split('?');
  const hashPath = hashParts[0] ?? '/';
  const hashParams = new URLSearchParams(hashParts[1] ?? '');

  // Only set lang param if language differs from browser default
  if (newLang === defaultLang) {
    hashParams.delete('lang');
  } else {
    hashParams.set('lang', newLang);
  }

  // Rebuild hash
  const newHash = hashParams.toString() ? `${hashPath}?${hashParams.toString()}` : hashPath;

  // Update URL without reloading
  window.history.replaceState({}, '', `#${newHash}`);
}

/**
 * Read language from hash parameter
 * @returns Language from hash or undefined if not set
 */
function getLanguageFromURL(): Language | undefined {
  if (typeof window === 'undefined') return undefined;

  // Parse hash parameters
  const hashParts = window.location.hash.slice(1).split('?');
  if (hashParts.length < 2) return undefined;

  const hashParams = new URLSearchParams(hashParts[1]);
  const langParam = hashParams.get('lang');

  if (langParam === 'en' || langParam === 'it') {
    return langParam;
  }

  return undefined;
}

/**
 * Initialize language from URL parameter if present
 * Should be called on app startup
 */
export function initializeLanguageFromURL() {
  const urlLang = getLanguageFromURL();
  if (urlLang) {
    localeSettings.set(urlLang);
  }
}

/**
 * Change the current language and update URL parameter
 * @param newLang - The new language to set
 */
export function changeLanguage(newLang: Language) {
  localeSettings.set(newLang);
  updateURLWithLanguage(newLang);
}

/**
 * Get the current language
 * @returns The current language code
 */
export function getCurrentLanguage(): Language {
  return locale.get();
}
